import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import MultipleSelector, { Option } from '@/components/ui/MultipleSelector';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTvControllers } from '@/lib/api/tvController';
import {
  TvController,
  TvControllerBrand,
  TvControllerStatus,
} from '@/lib/schema/tvController';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

type TvControllerFormProps = {
  tvControllerId?: string;
  tvController?: TvController;
  onSubmit: (data: any) => void;
  mutation: any;
};

const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
  disable: z.boolean().optional(),
});

const TvControllerFormSchema = z.object({
  name: z.string().min(2).max(100),
  ip: z.string().ip(),
  tags: z.array(optionSchema).min(1).default([]),
  status: z.nativeEnum(TvControllerStatus),
  brand: z.nativeEnum(TvControllerBrand),
});
type TvControllerForm = z.infer<typeof TvControllerFormSchema>;

function createTagOptions(tags: string[]): Option[] {
  return tags.map((tag) => ({ label: tag, value: tag }));
}

export function TvControllerForm({
  tvControllerId,
  tvController,
  onSubmit,
  mutation,
}: TvControllerFormProps) {
  const navigate = useNavigate();
  const [values] = useTvControllers();

  const OPTIONS = useMemo(() => {
    const tagSet = new Set<string>();
    values?.forEach((tvController) => {
      tvController.tags.forEach((tag) => tagSet.add(tag));
    });
    return createTagOptions(Array.from(tagSet));
  }, [values]);

  const form = useForm<TvControllerForm>({
    resolver: zodResolver(TvControllerFormSchema),
    defaultValues: {
      name: tvController?.name || '',
      ip: tvController?.ip || '',
      tags: createTagOptions(tvController?.tags || []),
      status: tvController?.status || TvControllerStatus.UNKNOWN,
      brand: tvController?.brand || TvControllerBrand.ZEEVEE,
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((value) => {
          if (
            values?.some(
              (v) =>
                v.ip === value.ip &&
                (!tvControllerId || v.id !== tvControllerId)
            )
          ) {
            form.setError('ip', {
              type: 'manual',
              message: 'IP address must be unique',
            });
            return;
          }

          if (
            values?.some(
              (v) =>
                v.name === value.name &&
                (!tvControllerId || v.id !== tvControllerId)
            )
          ) {
            form.setError('name', {
              type: 'manual',
              message: 'Name must be unique',
            });
            return;
          }
          onSubmit({ ...value, tags: value.tags.map((tag) => tag.value) });
        })}
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  inError={!!form.formState.errors.name}
                  placeholder="Enter controller name"
                  {...field}
                />
              </FormControl>
              <FormMessage>{fieldState.error?.message || ''}</FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ip"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>IP Address</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  inError={!!form.formState.errors.ip}
                  placeholder="192.168.1.1"
                  {...field}
                />
              </FormControl>
              <FormMessage>{fieldState.error?.message || ''}</FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tags"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <FormControl>
                <MultipleSelector
                  {...(field as any)}
                  options={OPTIONS}
                  placeholder="Add a tag ..."
                  inError={!!fieldState.error?.message}
                  creatable
                  emptyIndicator={
                    <p className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400">
                      The tag is already added
                    </p>
                  }
                />
              </FormControl>
              <FormMessage>{fieldState.error?.message || ''}</FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="brand"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Brand</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(TvControllerBrand).map((brand) => (
                    <SelectItem key={brand} value={brand}>
                      {brand}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage>{fieldState.error?.message || ''}</FormMessage>
            </FormItem>
          )}
        />

        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/tv-controllers')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {tvControllerId
              ? mutation.isPending
                ? 'Updating...'
                : 'Update TV Controller'
              : mutation.isPending
                ? 'Creating...'
                : 'Create TV Controller'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
